import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiTrendingDown, FiTrendingUp } from 'react-icons/fi';
import ReactTooltip from 'react-tooltip';
import dayjs from 'dayjs';
import { PreviousRange } from '../utils/getPreviousRange';
import styles from './KpiHeader.module.scss';

export type KpiHeaderProps = {
	value: string;
	subLabel: string;
	currentTotal: number;
	previousTotal: number | null;
	previousRange: PreviousRange | null;
	// Semantic direction of improvement: for costs/emissions, lower is better (default).
	// For usage metrics (km driven, visits), higher might be better.
	lowerIsBetter?: boolean;
	// When provided, the value + sub-label become clickable.
	onClick?: () => void;
};

const KpiHeader = ({
	value,
	subLabel,
	currentTotal,
	previousTotal,
	previousRange,
	lowerIsBetter = true,
	onClick,
}: KpiHeaderProps) => {
	const { t } = useTranslation();
	const [tooltipId] = useState(() => `kpi-trend-${Math.random().toString(36).slice(2, 10)}`);

	const hasPrevious = previousTotal !== null && previousTotal > 0;
	const trendPercent = hasPrevious ? ((currentTotal - (previousTotal as number)) / (previousTotal as number)) * 100 : null;
	const isDown = trendPercent !== null && trendPercent < 0;
	const isImproving = trendPercent !== null && (lowerIsBetter ? isDown : !isDown);

	const tooltip = previousRange
		? `${t(previousRange.mode === 'yoy' ? 'dashboard.compared_yoy' : 'dashboard.compared_previous_period' as any)}: ${dayjs(previousRange.prevFrom).format('MMM YYYY')} – ${dayjs(previousRange.prevTo).format('MMM YYYY')}`
		: '';

	const totalGroupProps = onClick
		? { onClick, role: 'button' as const, tabIndex: 0, className: `${styles.kpiTotalGroup} ${styles.kpiTotalGroupClickable}` }
		: { className: styles.kpiTotalGroup };

	return (
		<div className={styles.kpiHeader}>
			{trendPercent !== null && (
				<>
					<div
						className={`${styles.kpiBadge} ${isImproving ? styles.kpiBadgePositive : styles.kpiBadgeNegative}`}
						data-tip={tooltip}
						data-for={tooltipId}
					>
						{isDown ? <FiTrendingDown /> : <FiTrendingUp />}
						<span>
							{trendPercent > 0 ? '+' : ''}{trendPercent.toFixed(0)}% {t('dashboard.vs_previous_period' as any)}
						</span>
					</div>
					<ReactTooltip id={tooltipId} effect="solid" />
				</>
			)}
			<div {...totalGroupProps}>
				<div className={styles.kpiMain}>
					<div className={styles.kpiValue}>{value}</div>
					<div className={styles.kpiSubLabel}>{subLabel}</div>
				</div>
			</div>
		</div>
	);
};

export default KpiHeader;
